import { html } from 'lit';

if (!(globalThis as any).URLPattern) {
  await import("urlpattern-polyfill");
}

import { Context, Router } from '@thepassle/app-tools/router.js';
import { lazy } from '@thepassle/app-tools/router/plugins/lazy.js';
import { scrollToTop } from '@thepassle/app-tools/router/plugins/scrollToTop.js';
// @ts-ignore
import { redirect } from '@thepassle/app-tools/router/plugins/redirect.js';
import { encodeProductId } from './api/helpers';
import { ProductIdArgs, ProductIdsQueryArgs } from './api/models';
import { getProductsById, getSimilarProducts, searchProducts } from './api/products';
import { SHOPS, TITLES } from './constants';
import './pages/app-search/app-search';
import './pages/app-welcome/app-welcome';
import { prefetch, setMetaData, visitPage } from './router-plugins';
import { PAGE, selectProductSearchQueryArgs } from './state/pages';
import { getList, getSelectedListId, Product, ProductList } from './state/products';
import { prefetchProduct } from './api/product';
import { store } from './store';

// @ts-ignore
const baseURL: string = process.env.BASE_URL;


export const router: Router = new Router({
  fallback: "/",
  routes: [
    {
      path: resolveRouterPath("/"),
      title: TITLES.title,
      render: () => {
        return html`<app-welcome></app-welcome>`
      },
      plugins: [
        visitPage(PAGE.WELCOME),
        prefetch((_context: Context) => {
          const productSearchQuery = selectProductSearchQueryArgs(store.getState()) || {};
          store.dispatch(searchProducts(productSearchQuery));
        }),
        setMetaData((_: Context) => ({
          title: TITLES.title,
          description: TITLES.slogan,
          image: TITLES.banner,
          imageAlt: TITLES.bannerAlt
        })),
        scrollToTop,
      ]
    },
    {
      path: resolveRouterPath('/search'),
      title: `Zoekresultaten | ${TITLES.title}`,
      render: () => {
        return html`<app-search></app-search>`
      },
      plugins: [
        visitPage(PAGE.SEARCH),
        prefetch((_context: Context) => {
          const productSearchQuery = selectProductSearchQueryArgs(store.getState())
          store.dispatch(searchProducts(productSearchQuery));
        }),
        setMetaData((_: Context) => ({
          title: `Zoekresultaten | ${TITLES.title}`,
          description: TITLES.slogan,
          image: TITLES.banner,
          imageAlt: TITLES.bannerAlt
        }))
      ]
    },
    {
      path: resolveRouterPath('/shops/:shop_ids'),
      title: (context: Partial<Context>) => `${SHOPS[context?.params?.shop_id as keyof typeof SHOPS]?.name.toUpperCase()} PROMO VANDAAG | ${TITLES.title}`,
      render: () => {
        const productSearchQuery = selectProductSearchQueryArgs(store.getState())
        return html`<app-search .productSearchQuery=${productSearchQuery}></app-search>`
      },
      plugins: [
        visitPage(PAGE.SEARCH),
        prefetch((_context: Context) => {
          const productSearchQuery = selectProductSearchQueryArgs(store.getState())
          store.dispatch(searchProducts(productSearchQuery));
        }),
        setMetaData((context: Context) => ({
          title: `PROMOS vandaag bij ${SHOPS[context?.params?.shop_id as keyof typeof SHOPS]?.name.toUpperCase()} | ${TITLES.title}`,
          description: TITLES.slogan,
          image: TITLES.banner,
          imageAlt: TITLES.bannerAlt
        }))
      ]
    },
    {
      path: resolveRouterPath('/categories/:category_ids'),
      title: (context: Partial<Context>) => {
        const decodedCategory = decodeURIComponent((context?.params?.category_ids || "").replace(/%(?![0-9A-Fa-f]{2})/g, '%25'));
        return `${decodedCategory} | PROMOS | ${TITLES.title}`;
      },
      render: () => {
        const productSearchQuery = selectProductSearchQueryArgs(store.getState())
        return html`<app-search .productSearchQuery=${productSearchQuery}></app-search>`
      },
      plugins: [
        visitPage(PAGE.SEARCH),
        prefetch((_context: Context) => {
          const productSearchQuery = selectProductSearchQueryArgs(store.getState())
          store.dispatch(searchProducts(productSearchQuery));
        }),
        setMetaData((context: Context) => {
          const decodedCategory = decodeURIComponent((context?.params?.category_ids || "").replace(/%(?![0-9A-Fa-f]{2})/g, '%25'));
          return {
            title: `${decodedCategory} | PROMOS | ${TITLES.title}`,
            description: TITLES.slogan,
            image: TITLES.banner,
            imageAlt: TITLES.bannerAlt
          }
        })
      ]
    },
    {
      path: resolveRouterPath('/account'),
      title: 'Mijn account',
      plugins: [
        lazy(() => import('./pages/app-account/app-account.js')),
        visitPage(PAGE.ACCOUNT),
        scrollToTop,
      ],
      render: () => html`<app-account></app-account>`,
    },
    {
      path: resolveRouterPath('/account/list'),
      title: 'Watchlist',
      plugins: [
        lazy(() => import('./pages/app-watchlist.ts/app-watchlist.js')),
        visitPage(PAGE.WATCH_LIST),
        prefetch((_context: Context) => {
          const listId = getSelectedListId(store.getState());
          const list: ProductList | null = getList(store.getState(), listId);
          const ids: string[] = [];
          list?.products.forEach((product: Product) => {
            if (product.shopId && product.productRef) {
              ids.push(encodeProductId(product.shopId, product.productRef));
            }
          });
          console.info(`dispatching getProductsById for ${ids.length} products`);
          store.dispatch(getProductsById({ product_ids: ids } as ProductIdsQueryArgs));
        }),
        scrollToTop,
        setMetaData((_: Context) => {
          const listId = getSelectedListId(store.getState());
          const list: ProductList | null = getList(store.getState(), listId);
          return {
            title: `${list?.name} | ${TITLES.title}`,
            description: TITLES.slogan,
            image: TITLES.banner,
            imageAlt: TITLES.bannerAlt
          }
        }),
      ],
      render: (_context: Context) => {
        return html`<app-watchlist></app-watchlist>`
      },
    },
    {
      path: resolveRouterPath('/productid/:productId'),
      title: 'Product details',
      plugins: [
        visitPage(PAGE.PRODUCT),
        lazy(() => import('./pages/app-product/app-product.js')),
        prefetch((context: Context) => {
          store.dispatch(prefetchProduct({ product_id: context.params.productId } as ProductIdArgs));
          store.dispatch(getSimilarProducts({ product_id: context.params.productId } as ProductIdArgs));
        }),
        scrollToTop,
      ],
      render: () => {
        const params = router.context.params as {
          productId: string;
        };
        return html`<app-product product-id="${params.productId}" .query=${router.context.query}></app-product>`;
      }
    },
    {
      path: resolveRouterPath('/productid/:productId/:description'),
      title: "Product details",
      plugins: [
        lazy(() => import('./pages/app-product/app-product.js')),
        visitPage(PAGE.PRODUCT),
        prefetch((context: Context) => {
          store.dispatch(prefetchProduct({ product_id: context.params.productId } as ProductIdArgs));
          store.dispatch(getSimilarProducts({ product_id: context.params.productId } as ProductIdArgs));
        }),
        scrollToTop,
      ],
      render: () => {
        const params = router.context.params as {
          productId: string;
        };
        return html`<app-product product-id="${params.productId}" .query=${router.context.query}></app-product>`;
      }
    },
    {
      path: resolveRouterPath('/product/:shopId/:productRef'), // LEGACY ROUTE
      title: `Redirecting... | ${TITLES.title}`,
      plugins: [
        redirect((context: Context) => resolveRouterPath(`/productid/${encodeProductId(context.params.shopId, context.params.productRef)}`)),
      ],
    },
    {
      path: resolveRouterPath('/product.html'), // LEGACY ROUTE
      title: `Redirecting... | ${TITLES.title}`,
      plugins: [
        redirect((context: Context) => {
          const shopId = context.url.searchParams.get("s") || "";
          const productRef = context.url.searchParams.get("ref") || "";
          return resolveRouterPath(`/productid/${encodeProductId(shopId, productRef)}`);
        }),
      ],
    },
    {
      path: resolveRouterPath('/shop/:shopId'),  // LEGACY ROUTE
      title: `Redirecting... | ${TITLES.title}`,
      plugins: [
        redirect((context: Context) => {
          return resolveRouterPath(`/shop/${context.params.shopId}`);
        }),
      ],
    },
    {
      path: resolveRouterPath('/about'),
      title: 'About',
      plugins: [
        lazy(() => import('./pages/app-about/app-about.js')),
        visitPage(PAGE.ABOUT),
        scrollToTop,
      ],
      render: () => html`<app-about></app-about>`
    }
  ]
});

// This function will resolve a path with whatever Base URL was passed to the vite build process.
// Use of this function throughout the starter is not required, but highly recommended, especially if you plan to use GitHub Pages to deploy.
// If no arg is passed to this function, it will return the base URL.

export function resolveRouterPath(unresolvedPath?: string) {
  let resolvedPath = baseURL;
  if (unresolvedPath) {
    if (!unresolvedPath.startsWith("/")) unresolvedPath = `/${unresolvedPath}`;
    if (!unresolvedPath.startsWith(baseURL)) resolvedPath = resolvedPath + unresolvedPath;
    else resolvedPath = unresolvedPath;
  }
  return resolvedPath;
}
